/**
 * Page: EventFormPage
 * Page for creating new events and editing existing ones
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/authContext';
import { useToast } from '@/hooks/use-toast';
import { useEvent, useEventForm } from '../hooks';
import { EventForm } from '../components/EventForm';
import { getEventById, generateEventCode } from '@/data/eventRepository';
import { InlineLoader } from '@/components/ui/pulsing-loader';
import type { Event } from '@/types/event.types';
import type { Database } from '@/types/supabase';

type EventInsert = Database['public']['Tables']['events']['Insert'];
type EventUpdate = Database['public']['Tables']['events']['Update'];

export function EventFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { toast } = useToast();

  const isEditMode = Boolean(id);
  const [isSaving, setIsSaving] = useState(false);
  const [existingEvent, setExistingEvent] = useState<Event | null>(null);
  const [isLoadingEvent, setIsLoadingEvent] = useState(isEditMode);

  // Load existing event if editing
  useEffect(() => {
    if (id) {
      const loadEvent = async () => {
        setIsLoadingEvent(true);
        try {
          const event = await getEventById(id);
          if (event) {
            setExistingEvent(event);
          } else {
            // Event not found
            navigate('/events');
          }
        } catch (error) {
          console.error('Error loading event:', error);
          navigate('/events');
        } finally {
          setIsLoadingEvent(false);
        }
      };

      loadEvent();
    }
  }, [id, navigate]);

  // Form hook
  const {
    formData,
    errors,
    touched,
    flyerPreview,
    hasChanges,
    updateField,
    handleBlur,
    handleFlyerSelect,
    validateAll,
    codeStatus,
    applyGeneratedCode,
    resetCodeSuggestion,
    paymentQrPreview,
    handlePaymentQrSelect,
    handlePaymentQrRemove,
  } = useEventForm(existingEvent || undefined);

  // CRUD operations
  const {
    createEvent,
    updateEvent: updateEventAction,
    uploadEventFlyer,
    uploadPaymentQr,
    deletePaymentQr,
    isSubmitting,
  } = useEvent();

  // Default code for new events (EVT-YYYY-NNN). Runs once: it is replaced as soon as the
  // name is typed (suggestion) or the user types their own, and clearing the field
  // must not bring it back.
  const defaultCodeRequested = useRef(false);
  useEffect(() => {
    if (isEditMode || defaultCodeRequested.current) return;
    defaultCodeRequested.current = true;
    generateEventCode().then((code) => applyGeneratedCode(code));
  }, [isEditMode, applyGeneratedCode]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!user?.id) {
      console.error('User not authenticated');
      return;
    }

    // Validate form
    const isValid = validateAll();
    if (!isValid) {
      toast({
        variant: 'destructive',
        title: 'Error de validación',
        description:
          'Por favor corrige los errores en el formulario antes de continuar.',
      });
      return;
    }

    // Everything below (save + file uploads) runs behind one loading state; the
    // success toast and redirect only happen once all of it has finished.
    setIsSaving(true);
    try {
      // Bank payment QR: upload the new image or remove the existing one
      const syncPaymentQr = async (eventId: string) => {
        if (formData.payment_qr) {
          await uploadPaymentQr(eventId, formData.payment_qr, user.id);
        } else if (formData.remove_payment_qr && existingEvent?.payment_qr_filename) {
          await deletePaymentQr(eventId, existingEvent.payment_qr_filename, user.id);
        }
      };

      // Prepare data
      if (isEditMode && existingEvent) {
        // Update existing event
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const updateData: EventUpdate & Record<string, any> = {
          code: formData.code,
          name: formData.name,
          description: formData.description || null,
          location: formData.location || null,
          start_date: formData.start_date!.toISOString().split('T')[0],
          end_date: formData.end_date!.toISOString().split('T')[0],
          base_price: formData.base_price,
          agenda: formData.agenda || null,
          registration_form_type: formData.registration_form_type,
          allow_public_guests: formData.allow_public_guests,
          signature_mode: formData.signature_mode,
          registration_deadline: formData.registration_deadline
            ? new Date(formData.registration_deadline).toISOString()
            : null,
          registration_declaration: formData.registration_declaration.trim() || null,
          payment_instructions: formData.payment_instructions.trim() || null,
          updated_by: user.id,
        };

        const result = await updateEventAction(existingEvent.id, updateData, { silent: true });

        if (result) {
          // Upload new flyer if selected (replaces old one automatically)
          if (formData.flyer) {
            const flyerResult = await uploadEventFlyer(
              result.id,
              formData.flyer,
              user.id,
            );
            if (!flyerResult) {
              toast({
                variant: 'destructive',
                title: 'Error al cargar afiche',
                description:
                  'El evento fue actualizado pero falló la carga del afiche.',
              });
            }
          }

          await syncPaymentQr(result.id);

          toast({ title: 'Evento actualizado', description: 'Los cambios fueron guardados exitosamente.' });
          navigate(`/events/${result.id}`);
        }
      } else {
        // Create new event
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const insertData: EventInsert & Record<string, any> = {
          code: formData.code,
          name: formData.name,
          description: formData.description || null,
          location: formData.location || null,
          start_date: formData.start_date!.toISOString().split('T')[0],
          end_date: formData.end_date!.toISOString().split('T')[0],
          base_price: formData.base_price,
          agenda: formData.agenda || null,
          registration_form_type: formData.registration_form_type,
          allow_public_guests: formData.allow_public_guests,
          signature_mode: formData.signature_mode,
          registration_deadline: formData.registration_deadline
            ? new Date(formData.registration_deadline).toISOString()
            : null,
          registration_declaration: formData.registration_declaration.trim() || null,
          payment_instructions: formData.payment_instructions.trim() || null,
          status: 'DRAFT',
          created_by: user.id,
        };

        const result = await createEvent(insertData, { silent: true });

        if (result) {
          // Upload flyer if selected
          if (formData.flyer) {
            await uploadEventFlyer(result.id, formData.flyer, user.id);
          }
          await syncPaymentQr(result.id);

          toast({ title: 'Evento creado', description: `El evento "${formData.name}" fue creado exitosamente.` });
          navigate(`/events/${result.id}`);
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/events');
  };

  // Show loading while fetching event data
  if (isLoadingEvent) {
    return (
      <div className="container mx-auto flex justify-center py-20">
        <InlineLoader message="Cargando evento..." />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <EventForm
        formData={formData}
        errors={errors}
        touched={touched}
        isSubmitting={isSubmitting || isSaving}
        isEditMode={isEditMode}
        hasChanges={hasChanges}
        onFieldChange={updateField}
        onFieldBlur={handleBlur}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        flyerPreview={flyerPreview}
        onFlyerSelect={handleFlyerSelect}
        paymentQrPreview={paymentQrPreview}
        onPaymentQrSelect={handlePaymentQrSelect}
        onPaymentQrRemove={handlePaymentQrRemove}
        codeStatus={codeStatus}
        onSuggestCode={resetCodeSuggestion}
        // Registration links already shared stop working when the code changes
        codeChangeWarning={
          isEditMode && !!existingEvent && existingEvent.status !== 'DRAFT' && formData.code !== existingEvent.code
        }
      />
    </div>
  );
}
